import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../config/firebaseConfig";
import { doc, setDoc, getDoc } from "firebase/firestore";
import pdfToText from "react-pdftotext";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import styles from "../styling/formPage.module.css";
import joinStyles from "../styling/joinPage.module.css";

const LoadingScreen = () => (
  <div className={styles["loading-overlay"]}>
    <div className={styles["loading-content"]}>
      <div className={styles["loading-spinner"]}></div>
      <h2>Creating your profile...</h2>
      <p>This may take a moment while we process your information.</p>
    </div>
  </div>
);

function FormPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    industry: "",
    company: "",
    description: "",
    linkedin: "",
    searchable: true,
    externalLinks: [],
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [resumeError, setResumeError] = useState("");

  // New state for authentication
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  // Add authentication check on mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Check if user already has a profile
        const userDocRef = doc(db, "adonis_users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          navigate("/search");
          return;
        }

        // Only set the user state if we're not already processing auth
        if (!authLoading) {
          setUser(user);
          setFormData((prev) => ({
            ...prev,
            name: user.displayName || "",
            email: user.email || "",
          }));
        }
      } else {
        // User is not authenticated
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [navigate, authLoading]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      setSelectedFileName(file.name);
    }
  };

  const handleExternalLinkChange = (index, field, value) => {
    const updatedLinks = [...formData.externalLinks];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      externalLinks: updatedLinks,
    });
  };

  const addExternalLink = () => {
    setFormData({
      ...formData,
      externalLinks: [...formData.externalLinks, { title: "", url: "" }],
    });
  };

  const removeExternalLink = (index) => {
    const updatedLinks = formData.externalLinks.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      externalLinks: updatedLinks,
    });
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      await setPersistence(auth, browserLocalPersistence);

      let result;

      if (isLogin) {
        // Login
        result = await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Register
        result = await createUserWithEmailAndPassword(auth, email, password);
      }

      // Check for existing account
      const userDocRef = doc(db, "adonis_users", result.user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User already has a profile, redirect to search
        navigate("/search");
        return;
      }

      // User authenticated but no profile - set user state and show form
      setUser(result.user);
      setFormData((prev) => ({
        ...prev,
        name: result.user.displayName || "",
        email: result.user.email || "",
      }));
    } catch (error) {
      console.error("Authentication error:", error);
      setError(error.message || "Failed to authenticate");
    } finally {
      setAuthLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Remove the validation check that requires a resume file
    setResumeError("");
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const userEmail = user ? user.email : null;
      const uid = user ? user.uid : null;

      if (!userEmail || !uid) {
        setError("User is not authenticated. Please log in.");
        setLoading(false);
        return;
      }

      let resumeText = "";
      let parsedData = null;

      // Only process resume if one was uploaded
      if (resumeFile) {
        resumeText = await pdfToText(resumeFile);
        if (!resumeText) {
          throw new Error("Could not extract text from PDF");
        }

        console.log(
          "Extracted resume text:",
          resumeText.substring(0, 100) + "..."
        );

        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
        const parseResponse = await fetch(`${apiUrl}/api/parse-resume`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ resumeText }),
        });

        if (!parseResponse.ok) {
          const errorData = await parseResponse.json();
          throw new Error(errorData.error || "Failed to parse resume");
        }

        const responseData = await parseResponse.json();
        parsedData = responseData.parsedData;

        // Validate parsed data
        if (!parsedData) {
          throw new Error("Received invalid data from resume parser");
        }
      }

      const docData = {
        name: formData.name,
        email: userEmail,
        industry: formData.industry,
        company: formData.company,
        formData: {
          ...formData,
          email: userEmail,
        },
        resumeDetails: parsedData,
        timestamp: new Date(),
      };

      const userDocRef = doc(db, "adonis_users", uid);
      await setDoc(userDocRef, docData, { merge: true });

      navigate("/search", {
        state: {
          profileData: {
            career: formData.industry,
          },
          resumeText,
          parsedResume: parsedData,
        },
      });
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Only show form if user is authenticated
  if (!user) {
    return (
      <div className={joinStyles["container"]}>
        <div className={joinStyles["formWrapper"]}>
          <div className={joinStyles["logoWrapper"]}></div>

          <h1 className={joinStyles["title"]}>Welcome to Pantheon</h1>
          <p className={joinStyles["subtitle"]}>
            {isLogin ? "Sign in to continue" : "Create an account to continue"}
          </p>

          {error && <div className={joinStyles["error"]}>{error}</div>}

          <form onSubmit={handleAuthSubmit} className={joinStyles["auth-form"]}>
            <div className={joinStyles["form-group"]}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className={joinStyles["input"]}
              />
            </div>

            <div className={joinStyles["form-group"]}>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className={joinStyles["input"]}
                minLength={6}
              />
            </div>

            <button
              type="submit"
              className={joinStyles["signInButton"]}
              disabled={authLoading}
            >
              {authLoading
                ? "Processing..."
                : isLogin
                  ? "Sign In"
                  : "Create Account"}
            </button>
          </form>

          <div className={joinStyles["auth-toggle"]}>
            <p>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={toggleAuthMode}
                className={joinStyles["toggle-button"]}
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </div>

          <p className={joinStyles["terms"]}>
            By clicking continue, you agree to our{" "}
            <a href="/terms" className={joinStyles["link"]}>
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy" className={joinStyles["link"]}>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={styles["form-page"]}>
      <div className={styles["form-container"]}>
        <h1>One more thing.</h1>
        <h3>These questions will be used to create your user profile.</h3>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles["form-group"]}>
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Satoshi Nakomoto*"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles["form-group"]}>
            <label htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              name="company"
              placeholder="Your company or organization"
              value={formData.company}
              onChange={handleChange}
            />
          </div>

          <div className={styles["form-group"]}>
            <label htmlFor="description">Brief Introduction</label>
            <textarea
              id="description"
              name="description"
              placeholder="Tell us about yourself, your interests, and what you're building...*"
              value={formData.description}
              onChange={handleChange}
              required
              maxLength={300}
              rows={4}
              className={styles["description-input"]}
            />
            <div className={styles["char-count"]}>
              {formData.description.length}/300
            </div>
          </div>

          <div className={styles["form-group"]}>
            <label htmlFor="industry">Industry</label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
            >
              <option value="">Select your industry*</option>
              <option value="Business & Finance">Business & Finance</option>
              <option value="Engineering & Manufacturing">
                Engineering & Manufacturing
              </option>
              <option value="Technology & Software">
                Technology & Software
              </option>
              <option value="Healthcare & Science">Healthcare & Science</option>
              <option value="Marketing & Media">Marketing & Media</option>
              <option value="Education & Research">Education & Research</option>
              <option value="Government & Law">Government & Law</option>
              <option value="Retail & Hospitality">Retail & Hospitality</option>
              <option value="Real Estate & Construction">
                Real Estate & Construction
              </option>
              <option value="Energy & Environment">Energy & Environment</option>
              <option value="Arts & Entertainment">Arts & Entertainment</option>
              <option value="Sports & Wellness">Sports & Wellness</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className={styles["form-group"]}>
            <label htmlFor="linkedin">LinkedIn Profile</label>
            <input
              id="linkedin"
              type="url"
              name="linkedin"
              placeholder="https://linkedin.com/in/username"
              value={formData.linkedin}
              onChange={handleChange}
              pattern="https?:\/\/(www\.)?linkedin\.com\/in\/.*"
              title="Please enter a valid LinkedIn profile URL"
            />
          </div>

          <div className={styles["form-group"]}>
            <label htmlFor="resume">Upload Resume (PDF) (Optional)</label>
            <div
              className={styles["file-upload"]}
              onClick={() => document.getElementById("resume").click()}
            >
              {selectedFileName ? (
                <div className={styles["file-selected"]}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                    <polyline points="13 2 13 9 20 9"></polyline>
                  </svg>
                  <span>{selectedFileName}</span>
                </div>
              ) : (
                <div className={styles["file-prompt"]}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>Click to upload your resume (PDF) (Optional)</span>
                </div>
              )}
              <input
                id="resume"
                name="resume"
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </div>
            {resumeError && (
              <div className={styles["resume-error"]}>{resumeError}</div>
            )}
          </div>

          <div className={styles["form-group"]}>
            <div className={styles["checkbox-group"]}>
              <input
                type="checkbox"
                id="searchable"
                name="searchable"
                checked={formData.searchable}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    searchable: e.target.checked,
                  }))
                }
              />
              <label htmlFor="searchable">
                Allow others to see your profile.
              </label>
            </div>
          </div>

          <div className={styles["form-group"]}>
            <label>External Links</label>
            {formData.externalLinks.map((link, index) => (
              <div key={index} className={styles["external-link-group"]}>
                <input
                  type="text"
                  placeholder="Link Title (e.g., Portfolio, GitHub)"
                  value={link.title}
                  onChange={(e) =>
                    handleExternalLinkChange(index, "title", e.target.value)
                  }
                  className={styles["external-link-input"]}
                />
                <input
                  type="url"
                  placeholder="https://..."
                  value={link.url}
                  onChange={(e) =>
                    handleExternalLinkChange(index, "url", e.target.value)
                  }
                  pattern="https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)"
                  title="Please enter a valid URL starting with http:// or https://"
                  className={styles["external-link-input"]}
                />
                <button
                  type="button"
                  onClick={() => removeExternalLink(index)}
                  className={styles["remove-link-button"]}
                  aria-label="Remove link"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addExternalLink}
              className={styles["add-link-button"]}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add External Link
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={styles["form-btn"]}
          >
            {loading ? (
              "Creating profile..."
            ) : (
              <>
                Get Started{" "}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    width: "1.25em",
                    height: "1.25em",
                    verticalAlign: "middle",
                    transform: "translateY(-1.35px)",
                  }}
                >
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    <path
                      d="M7 17L17 7M17 7H8M17 7V16"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </g>
                </svg>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FormPage;
